import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Facebook, Instagram, Send, Globe } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { api } from "../lib/api";
import { waLink } from "../lib/site";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 0 1 0-5.18c.27 0 .53.04.77.12V9.69a5.7 5.7 0 0 0-.77-.05A5.68 5.68 0 1 0 15.54 15.3V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.28 4.28 0 0 1-3.24-1.48z" />
  </svg>
);

export const Contact = ({ contact = {}, socials = {} }) => {
  const [form, setForm] = useState({ name: "", phone: "", company: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const phone = contact.whatsapp || "50496390722";

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Por favor ingresa tu nombre");
      return;
    }
    setSending(true);
    try {
      await api.post("/public/leads", form);
    } catch (_) {
      /* lead save is best-effort; continue to WhatsApp */
    }
    setSending(false);
    const msg =
      `Hola GLG Technology, mi nombre es ${form.name}.` +
      (form.service ? `\nEstoy interesado en ${form.service}.` : "") +
      (form.message ? `\nMi proyecto consiste en ${form.message}` : "") +
      (form.company ? `\nEmpresa: ${form.company}` : "") +
      (form.phone ? `\nTeléfono: ${form.phone}` : "");
    window.open(waLink(msg, phone), "_blank", "noopener,noreferrer");
    toast.success("¡Gracias! Te redirigimos a WhatsApp.");
  };

  const items = [
    { icon: Phone, label: "Teléfono", value: contact.phone || "+504 9639-0722", href: `tel:+${phone}`, testid: "contact-phone" },
    { icon: MessageCircle, label: "WhatsApp", value: contact.phone || "+504 9639-0722", href: waLink(undefined, phone), testid: "contact-whatsapp", external: true },
    { icon: Mail, label: "Correo", value: contact.email || "info@glghn.com", href: `mailto:${contact.email || "info@glghn.com"}`, testid: "contact-email" },
    { icon: MapPin, label: "Ubicación", value: contact.location || "Honduras", testid: "contact-location" },
  ];

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-glg-black/60 px-4 py-3 text-sm text-white outline-none transition-colors duration-200 placeholder:text-white/30 focus:border-glg-blue";

  return (
    <section id="contacto" data-testid="contact-section" className="noise relative overflow-hidden bg-glg-black py-24 sm:py-32">
      <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 rounded-full bg-glg-blue/10 blur-[140px]" />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal><p className="text-xs font-semibold uppercase tracking-[0.3em] text-glg-blue">Contacto</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-heading text-3xl font-semibold leading-tight tracking-tight text-white sm:text-5xl">
                Conversemos sobre tu proyecto
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 max-w-md text-[15px] leading-relaxed text-white/55 sm:text-base">
                Cuéntanos qué necesitas y nuestro equipo te ayudará a encontrar la solución adecuada.
              </p>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <a href={`tel:+${phone}`} data-testid="contact-action-call" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:border-glg-blue">
                  <Phone className="h-4 w-4 text-glg-blue" /> Llamar
                </a>
                <a href={waLink(undefined, phone)} target="_blank" rel="noopener noreferrer" data-testid="contact-action-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-glg-green px-4 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03]">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
                <a href={`mailto:${contact.email || "info@glghn.com"}`} data-testid="contact-action-email" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:border-glg-orange">
                  <Mail className="h-4 w-4 text-glg-orange" /> Enviar correo
                </a>
              </div>
            </Reveal>

            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              {items.map((c) => {
                const Icon = c.icon;
                const inner = (
                  <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 transition-colors duration-300 hover:border-glg-blue/40">
                    <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-glg-blue/15">
                      <Icon className="h-5 w-5 text-glg-blue" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-white/40">{c.label}</div>
                      <div className="truncate text-sm font-medium text-white">{c.value}</div>
                    </div>
                  </div>
                );
                return c.href ? (
                  <a key={c.label} href={c.href} data-testid={c.testid} target={c.external ? "_blank" : undefined} rel={c.external ? "noopener noreferrer" : undefined}>{inner}</a>
                ) : (
                  <div key={c.label} data-testid={c.testid}>{inner}</div>
                );
              })}
            </div>

            <div className="mt-8 flex items-center gap-3">
              <span className="text-xs uppercase tracking-wider text-white/40">Síguenos</span>
              {socials.facebook && (
                <a href={socials.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors duration-200 hover:border-glg-blue hover:text-glg-blue"><Facebook className="h-4 w-4" /></a>
              )}
              {socials.instagram && (
                <a href={socials.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors duration-200 hover:border-glg-orange hover:text-glg-orange"><Instagram className="h-4 w-4" /></a>
              )}
              {socials.tiktok && (
                <a href={socials.tiktok} target="_blank" rel="noopener noreferrer" data-testid="social-tiktok" aria-label="TikTok" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors duration-200 hover:border-glg-green hover:text-glg-green"><TikTokIcon className="h-4 w-4" /></a>
              )}
            </div>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-white/10 bg-glg-surface/70 p-7 backdrop-blur-sm sm:p-9">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-white/50">Nombre</label>
                  <input name="name" value={form.name} onChange={onChange} data-testid="contact-input-name" placeholder="Tu nombre" className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-white/50">Teléfono / WhatsApp</label>
                  <input name="phone" value={form.phone} onChange={onChange} data-testid="contact-input-phone" placeholder="+504 ..." className={inputCls} />
                </div>
              </div>
              <div className="mt-5">
                <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-white/50">Empresa (opcional)</label>
                <input name="company" value={form.company} onChange={onChange} data-testid="contact-input-company" placeholder="Nombre de tu empresa" className={inputCls} />
              </div>
              <div className="mt-5">
                <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-white/50">Servicio de interés</label>
                <input name="service" value={form.service} onChange={onChange} data-testid="contact-input-service" placeholder="Ej. Sistema CCTV, control de acceso..." className={inputCls} />
              </div>
              <div className="mt-5">
                <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-white/50">Mensaje</label>
                <textarea name="message" value={form.message} onChange={onChange} data-testid="contact-input-message" rows={4} placeholder="Cuéntanos sobre tu proyecto" className={`${inputCls} resize-none`} />
              </div>
              <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-glg-green px-6 py-4 text-sm font-semibold text-white shadow-lg shadow-glg-green/25 transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60">
                <Send className="h-4 w-4" />
                {sending ? "Enviando..." : "Enviar por WhatsApp"}
              </button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-white/35">
                <Globe className="h-3 w-3" /> Al enviar se abrirá WhatsApp con tu mensaje listo.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
